import { Button } from "components/ui/Button";
import {
  type CreateDeliverySeriesInput,
  type DeliverySeries,
  type DeliverySeriesStatus,
  type UpdateDeliverySeriesInput,
  useCreateDeliverySeries,
  useUpdateDeliverySeries,
} from "hooks/use-delivery-series-api";
import { type FC, type FormEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { toastError } from "utils/toast-utils";

export type DeliverySeriesFormModalProps =
  | {
      isOpen: boolean;
      mode: "create";
      onClose: () => void;
      onSaved: (series: DeliverySeries) => void;
    }
  | {
      isOpen: boolean;
      mode: "edit";
      series: DeliverySeries | null;
      onClose: () => void;
      onSaved: (series: DeliverySeries) => void;
    };

/**
 * 配信シリーズの新規作成 / 編集モーダル.
 * mode: "create" は POST、 "edit" は PATCH を呼ぶ.
 * @param {DeliverySeriesFormModalProps} props props
 * @return {JSX.Element|null} モーダル or null
 */
export const DeliverySeriesFormModal: FC<
  DeliverySeriesFormModalProps
> = props => {
  const { isOpen, onClose, onSaved } = props;
  const editingSeries = props.mode === "edit" ? props.series : null;
  const { create, loading: creating } = useCreateDeliverySeries();
  const { update, loading: updating } = useUpdateDeliverySeries();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<DeliverySeriesStatus>("active");

  // モーダルを開いた時に初期値をセット
  useEffect(() => {
    if (!isOpen) return;
    if (editingSeries) {
      setName(editingSeries.name);
      setDescription(editingSeries.description ?? "");
      setStatus(editingSeries.status);
    } else {
      setName("");
      setDescription("");
      setStatus("active");
    }
  }, [isOpen, editingSeries]);

  if (!isOpen) return null;

  const submitting = creating || updating;
  const title = props.mode === "create" ? "新規シリーズ" : "シリーズを編集";

  /**
   * フォーム送信
   * @param {FormEvent} e フォームイベント
   * @return {Promise<void>}
   */
  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      toastError("シリーズ名を入力してください");
      return;
    }
    try {
      if (props.mode === "create") {
        const input: CreateDeliverySeriesInput = {
          name: name.trim(),
          description: description.trim() || null,
        };
        const created = await create(input);
        toast.success("シリーズを作成しました");
        onSaved(created);
      } else if (editingSeries) {
        const input: UpdateDeliverySeriesInput = {
          name: name.trim(),
          description: description.trim() || null,
          status,
        };
        const updated = await update(editingSeries.id, input);
        toast.success("シリーズを更新しました");
        onSaved(updated);
      }
    } catch {
      toastError("保存に失敗しました");
    }
  };

  // Layout の overflow-hidden / scroll container により modal-backdrop が
  // 全画面を覆えないため、 body 直下に portal して fixed 位置決めを保証する.
  return createPortal(
    <div className="modal modal-open">
      <div className="modal-box max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base">{title}</h3>
          <button
            type="button"
            className="text-base-content/60 hover:text-base-content"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-base-content/60">シリーズ名 *</span>
            <input
              type="text"
              className="input input-bordered input-sm w-full mt-1"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="例) 2026/04/26 人材A 営業"
              maxLength={255}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs text-base-content/60">説明 (任意)</span>
            <textarea
              className="textarea textarea-bordered textarea-sm w-full mt-1"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="このシリーズの目的・運用メモなど"
            />
          </label>
          {props.mode === "edit" && (
            <label className="block">
              <span className="text-xs text-base-content/60">ステータス</span>
              <select
                className="select select-bordered select-sm w-full mt-1"
                value={status}
                onChange={e =>
                  setStatus(e.target.value as DeliverySeriesStatus)
                }
              >
                <option value="active">active</option>
                <option value="archived">archived</option>
              </select>
            </label>
          )}
          <div className="text-[11px] text-base-content/50">
            ※ talent / focus_matter の紐付けは将来対応 (現在は CRM 側から設定)
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? "保存中..." : "保存"}
            </Button>
          </div>
        </form>
      </div>
      <button
        type="button"
        aria-label="閉じる"
        className="modal-backdrop"
        onClick={onClose}
      />
    </div>,
    document.body,
  );
};
